export class GMapsManager {
    /*************** Private properties ***************/
    private theMap!: google.maps.Map;
    private theMapCanvas!: HTMLElement;
    private contextMenuElement!: HTMLElement;
    private group2polylineMenuElement: { [group: string]: HTMLElement } = {};
    private fullScreen: boolean = false;

    private marker2data: { [id: number]: any } = {};
    private markers: { [id: number]: google.maps.Marker } = {};
    private polylines: { [id: number]: google.maps.Polyline } = {};
    private polylineId2vertexMarkers: { [id: number]: google.maps.Marker[] } = {};
    private polylineVertexDragListeners: Array<(...args: any[]) => void> = [];
    private polylineVertexDeletedListeners: Array<(...args: any[]) => void> = [];
    private deletePolylineVertexEnabled: boolean = false;

    private circle2data: { [id: number]: any } = {};
    private circles: { [id: number]: google.maps.Circle } = {};

    private drawingManager!: google.maps.drawing.DrawingManager;
    private drawingControlListeners: Array<(...args: any[]) => void> = [];
    private polygons: google.maps.Polygon[] = [];

    private customControlId: number = 0;
    private customControls: { [id: number]: HTMLElement } = {};

    private clickListeners: Array<(lat: number, lng: number) => void> = [];
    private rightClickListeners: Array<(lat: number, lng: number) => void> = [];
    private polylineClickListeners: Array<(data: any) => void> = [];
    private polygonClickListeners: Array<(data: any) => void> = [];
    private markerDragListeners: Array<(data: any, lat: number, lng: number) => void> = [];

    // private infowindow: google.maps.InfoWindow = new google.maps.InfoWindow({});
    private group2infoWindowContent: { [group: string]: HTMLElement } = {};

    private markerId: number = 1;
    private polylineId: number = 1;
    private circleId: number = 1;

    private menuItemId: number = 1;
    private menuItemId2item: { [id: number]: any } = {};

    /*************** Constants ***************/
    // private readonly CONTROL_POSITION = google.maps.ControlPosition.TOP_CENTER;
    // private readonly DRAWING_MODS = [google.maps.drawing.OverlayType.POLYGON];
    // private readonly POLYGON_OPTIONS: google.maps.PolygonOptions = {
    //     editable: false,
    //     strokeColor: '#4c4c4c',
    //     strokeOpacity: 0.8,
    //     strokeWeight: 2,
    //     fillColor: '#a0a0a0',
    //     fillOpacity: 0.35,
    //     clickable: true
    // };

    private readonly MarkerVertexAction = Object.freeze({
        'Delete': 'Delete',
        'Hide': 'Hide',
        'Show': 'Show'
    });

    private readonly PopupType = Object.freeze({
        'Polyline': 'Polyline',
        'ContextMenu': 'ContextMenu'
    });

    private readonly MENU_ITEM_TYPE_MARKER = 'Marker';
    private readonly MENU_ITEM_TYPE_CONTEXT = 'Context';
    private readonly MENU_ITEM_TYPE_POLYLINE = 'Polyline';
    private readonly MENU_ITEM_TYPE_POLYGON = 'Polygon';
    private readonly MENU_ITEM_CLASS = 'menu-item';
    private readonly CONTEXT_MENU_CLASS = 'map-menu';

    private readonly MENU_ITEM_DEFAULT_GROUP = 'DEFAULT';
    private readonly MENU_ITEM_POLYLINE_VERTEX_GROUP = 'POLYLINE_VERTEXES';

    private readonly DATA_LAT_LON = 'latlon';
    private readonly DATA_INDEX = 'index';
    private readonly DATA_ID = 'id';
    private readonly DATA = 'data';

    private readonly ATTR_DATA_MENU_ITEM_ID = 'menu-item-id';
    private readonly ATTR_MARKER_ID = 'marker-id';
    private readonly ATTR_POLYGON_ID = 'polygon-id';
    private readonly ATTR_GROUP = 'group';
    private readonly ATTR_TYPE = 'type';

    /*************** Public Methods ***************/
    public init(map: google.maps.Map) {
        this.theMap = map;
        this.theMapCanvas = document.getElementById('map_canvas') as HTMLElement;

        this.setEventListeners();
        this.setupContextMenu();

        this.evalPolylineOptions({ group: this.MENU_ITEM_DEFAULT_GROUP });
        this.group2infoWindowContent[this.MENU_ITEM_DEFAULT_GROUP] = this.buildInfoWindowElement();

        // this.addPolylineMenuItem({
        //     text: 'delete vertex',
        //     clickListener: this.onDeletePolylineVertexClicked.bind(this),
        //     group: this.MENU_ITEM_POLYLINE_VERTEX_GROUP
        // });
    }

    public addPolyline(coordinates: google.maps.LatLng[], color?: string, data?: any, options?: any) {
        this.polylineId++;
        options = this.evalPolylineOptions(options);

        const icons: google.maps.IconSequence[] = [];
        if (!options.hideArrow) {
            icons.push({ icon: { path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW }, offset: '100%' });
        }

        const polyline = new google.maps.Polyline({
            path: coordinates,
            geodesic: true,
            strokeColor: color ?? '#FF0000',
            strokeOpacity: 1.0,
            strokeWeight: 4,
            zIndex: 11,
            icons: icons,
        });

        polyline.setMap(this.theMap);

        // google.maps.event.addListener(polyline, 'click', () => {
        //     this.polylineClickListeners.forEach(listener => listener(polyline.data));
        // });

        this.polylines[this.polylineId] = polyline;
        return polyline;
    }

    public hidePolyline(polyline: google.maps.Polyline) {
        polyline.setMap(null);
    }

    public showPolyline(polyline: google.maps.Polyline) {
        polyline.setMap(this.theMap);
    }

    /*************** Private Methods ***************/
    private buildInfoWindowElement(): HTMLElement {
        const infoWindowElement = document.createElement('div');
        infoWindowElement.classList.add(this.CONTEXT_MENU_CLASS);
        return infoWindowElement;
    }

    private evalPolylineOptions(options: any = {}): any {
        if (!options.group) options.group = this.MENU_ITEM_DEFAULT_GROUP;

        if (!this.group2polylineMenuElement[options.group]) {
            const menuElement = document.createElement('div');
            menuElement.classList.add(this.CONTEXT_MENU_CLASS);
            menuElement.setAttribute(this.ATTR_GROUP, options.group);
            menuElement.setAttribute(this.ATTR_TYPE, options.group);

            // this.theMap.getDiv().querySelector('div')?.appendChild(menuElement);
            this.group2polylineMenuElement[options.group] = menuElement;
        }

        return options;
    }

    private setEventListeners() {
        google.maps.event.addListener(this.theMap, 'click', (event: any) => {
            this.clickListeners.forEach(listener => listener(event.latLng.lat(), event.latLng.lng()));
        });
    }

    private setupContextMenu() {
        this.contextMenuElement = document.createElement('div');
        this.contextMenuElement.classList.add(this.CONTEXT_MENU_CLASS);
        this.theMap.getDiv().querySelector('div')?.appendChild(this.contextMenuElement);
    }

    private onDeletePolylineVertexClicked(index: number, data: any) {
        this.polylineVertexDeletedListeners.forEach(listener => listener(data, index));
    }

    private setPopupVisibility(element: HTMLElement, visible: boolean) {
        element.style.display = visible ? 'block' : 'none';
        element.style.visibility = visible ? 'visible' : 'hidden';
    }

    private buildMenuItemElement(item: any, type: string): HTMLElement {
        this.menuItemId++;
        item.type = type;
        this.menuItemId2item[this.menuItemId] = item;

        const aElement = document.createElement('a');
        const divElement = document.createElement('div');
        divElement.classList.add(this.MENU_ITEM_CLASS);
        divElement.textContent = item.text;
        divElement.setAttribute(this.ATTR_DATA_MENU_ITEM_ID, this.menuItemId.toString());
        divElement.setAttribute(this.ATTR_GROUP, item.group);
        aElement.appendChild(divElement);
        return aElement;
    }
}
